import React from 'react';
import { useTranslation } from 'react-i18next';
import css from '../../summary-view.module.css';
import SummaryGraph from '../../summary-graph';
import { formatDateValue } from '../../../../../services/utils';
import { DryPeriodChartEntry, DryPeriodEntry } from '../../../../../interfaces/livestock-reproduction';

interface ReproductionData {
  shortDryPeriod: DryPeriodEntry[];
  longDryPeriod: DryPeriodEntry[];
  goalBelow: number | null;
  goalAbove: number | null;
  dryPeriodChart: DryPeriodChartEntry[];
}

export default function DryPeriodSummary({
  reproductionData,
  title
}: {
  reproductionData: ReproductionData;
  title: string;
}) {

  const { t } = useTranslation();

  const calculateAverageDryPeriod = () => {
    if (!reproductionData.shortDryPeriod || !reproductionData.longDryPeriod) {
      return 0;
    }

    const shortDryPeriods = reproductionData.shortDryPeriod.map(data => data.dryPeriod);
    const longDryPeriods = reproductionData.longDryPeriod.map(data => data.dryPeriod);
    
    const allDryPeriods = [...shortDryPeriods, ...longDryPeriods];
  
    if (allDryPeriods.length === 0) {
      return 0;
    }

    const totalDryPeriods = allDryPeriods.reduce((total, dryPeriod) => total + dryPeriod, 0);
    const averageDryPeriod = totalDryPeriods / allDryPeriods.length;
  
    return averageDryPeriod.toFixed(2);
  };

  return (
    <div className={css['summary-container']}>
      <div className={css['left-panel']}>
        <div className={css['grid-container-smaller']}>
          <SummaryGraph
            title={title}
            height={193}
            xKey="interval"
            yLeftValue={t('pages.livestock.summary.view.low_yield_cow')}
            barObjects={[
              {
                dataKey: "thisYear",
                dataTitle: t('pages.livestock.summary.view.this_year'),
                strokeColor: "#12589F",
                yAxis: "left",
              },
              {
                dataKey: "prevYear",
                dataTitle: t('pages.livestock.summary.view.prev_year'),
                strokeColor: "#bbbaba",
                yAxis: "left",
              },
            ]}
            data={reproductionData.dryPeriodChart}
            xTitle={t('pages.livestock.summary.view.dry_period_in_days')}
          />
          <p className={css['summary']}>{t('pages.livestock.summary.view.dry_period_this_year')}</p>

          <table className={css['under-table']}>
            <tbody className={css['strong']}>
              <tr>
                <td>{t('pages.livestock.summary.view.goal_below')}</td>
                <td>{reproductionData.goalBelow}%</td>
                <td>{t('pages.livestock.summary.view.goal_dry_period')}</td>
                <td>10%</td>
              </tr>

              <tr>
                <td>{t('pages.livestock.summary.view.goal_above')}</td>
                <td>{reproductionData.goalAbove}%</td>
                <td>{t('pages.livestock.summary.view.goal_dry_period')}</td>
                <td>20%</td>
              </tr>
            </tbody>
          </table>

          <p className={css['summary']}>{t('pages.livestock.summary.view.average_dry_period_length')}</p>

          <table className={css['under-table']}>
            <tbody className={css['strong']}>
              <tr style={{ backgroundColor: 'rgb(242, 242, 242)' }}>
                <td>{t('pages.livestock.summary.view.dry_period_days_table')}</td>
                <td>{calculateAverageDryPeriod()}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className={css['left-panel']}>
        <p className={css['dry-period-title']}>{t('pages.livestock.summary.view.short_dry_period_cow')}</p>
        <table className={css['table']} style={{ width: '93%' }}>
          <thead>
            <tr>
              <th>{t('pages.livestock.summary.view.cow_number')}</th>
              <th>{t('pages.livestock.summary.view.cow_lactation_number')}</th>
              <th>{t('pages.livestock.summary.view.dry_period_days')}</th>
              <th>{t('pages.livestock.summary.view.interbreeding_interval')}</th>
              <th>{t('pages.livestock.summary.view.last_release_date')}</th>
              <th>{t('pages.livestock.summary.view.last_calving_date')}</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(reproductionData.shortDryPeriod).map(([index, data]) => (
              <tr key={index}>
                <td>{data.number}</td>
                <td>{data.lactation}</td>
                <td>{data.dryPeriod}</td>
                <td>{data.interbreeding}</td>
                <td>{formatDateValue(data.releaseDate)}</td>
                <td>{formatDateValue(data.calvingDate)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <p className={css['dry-period-title']}>{t('pages.livestock.summary.view.long_dry_period_cow')}</p>
        <table className={css['table']} style={{ width: '93%' }}>
          <thead>
            <tr>
              <th>{t('pages.livestock.summary.view.cow_number')}</th>
              <th>{t('pages.livestock.summary.view.cow_lactation_number')}</th>
              <th>{t('pages.livestock.summary.view.dry_period_days')}</th>
              <th>{t('pages.livestock.summary.view.interbreeding_interval')}</th>
              <th>{t('pages.livestock.summary.view.last_release_date')}</th>
              <th>{t('pages.livestock.summary.view.last_calving_date')}</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(reproductionData.longDryPeriod).map(([index, data]) => (
              <tr key={index}>
                <td>{data.number}</td>
                <td>{data.lactation}</td>
                <td>{data.dryPeriod}</td>
                <td>{data.interbreeding}</td>
                <td>{formatDateValue(data.releaseDate)}</td>
                <td>{formatDateValue(data.calvingDate)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
